/**
 * Planner Day Marker
 *
 * Parses inline day-planner markers emitted in Losi chat responses.
 *
 * Primary responsibilities:
 * - Validate day-planner marker payloads from assistant text
 * - Normalize proposed planner items into stable event-like records
 * - Serialize safe markers for prompt/tool contracts
 *
 * Dependencies:
 * - Native Date and JSON APIs
 */

export const LOSI_PLANNER_DAY_MARKER_REGEX =
  /\\?\[LOSI_PLANNER_DAY\\?\]([\s\S]*?)\\?\[\/LOSI_PLANNER_DAY\\?\]/g

export type LosiPlannerDayItemKind = 'event' | 'task' | 'focus' | 'break'
export type LosiPlannerDayItemStatus = 'accepted' | 'cancelled' | 'proposed'
export type LosiPlannerDayItemSource = 'apple' | 'google' | 'losi' | 'space' | 'task'

export interface LosiPlannerDayMarkerItem {
  allDay?: boolean
  description?: string
  end: string
  eventId?: string
  id: string
  kind: LosiPlannerDayItemKind
  location?: string
  source: LosiPlannerDayItemSource
  spaceId?: string
  start: string
  status: LosiPlannerDayItemStatus
  taskId?: string
  title: string
}

export interface LosiPlannerDayMarkerPayload {
  date: string
  items: LosiPlannerDayMarkerItem[]
  spaceId?: string
  subtitle?: string
  timezone?: string
  title?: string
  viewEnd?: string
  viewStart?: string
}

export interface LosiPlannerDayMarkerMatch {
  payload: LosiPlannerDayMarkerPayload
  rawText: string
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_ONLY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const MAX_ITEMS = 24
const MAX_TEXT_LENGTH = 160
const MAX_LONG_TEXT_LENGTH = 700
const DEFAULT_DURATION_MINUTES = 60
const ITEM_KIND_SET = new Set<LosiPlannerDayItemKind>(['break', 'event', 'focus', 'task'])
const ITEM_SOURCE_SET = new Set<LosiPlannerDayItemSource>([
  'apple',
  'google',
  'losi',
  'space',
  'task'
])
const ITEM_STATUS_SET = new Set<LosiPlannerDayItemStatus>([
  'accepted',
  'cancelled',
  'proposed'
])

type ParsedPlannerDateTime = {
  fromTimeOnly: boolean
  iso: string
}

// --------------

export function parseLosiPlannerDayMarkerPayload(
  payloadText: string
): LosiPlannerDayMarkerPayload | null {
  const parsed = parseJsonObject(stripJsonFence(normalizeEscapedJson(payloadText)))
  if (!parsed) return null

  const date = readDateKey(parsed.date)
  if (!date) return null

  const defaultSpaceId = readTextField(parsed, 'spaceId', MAX_TEXT_LENGTH)
  const items = readPlannerItems(parsed.items, date, defaultSpaceId)
  if (items.length === 0) return null

  const title = readTextField(parsed, 'title', MAX_TEXT_LENGTH)
  const subtitle = readTextField(parsed, 'subtitle', MAX_LONG_TEXT_LENGTH)
  const timezone = readTextField(parsed, 'timezone', MAX_TEXT_LENGTH)
  const viewStart = readDateTimeField(parsed, date, [
    'viewStart',
    'view_start',
    'visibleStart',
    'visible_start',
    'plannerStart',
    'planner_start'
  ])
  const rawViewEnd = readDateTimeField(parsed, date, [
    'viewEnd',
    'view_end',
    'visibleEnd',
    'visible_end',
    'plannerEnd',
    'planner_end'
  ])
  const viewEnd = viewStart ? normalizeEndDate(viewStart.iso, rawViewEnd) : rawViewEnd?.iso

  return {
    date,
    items,
    ...(defaultSpaceId ? { spaceId: defaultSpaceId } : {}),
    ...(subtitle ? { subtitle } : {}),
    ...(timezone ? { timezone } : {}),
    ...(title ? { title } : {}),
    ...(viewEnd ? { viewEnd } : {}),
    ...(viewStart ? { viewStart: viewStart.iso } : {})
  }
}

export function findLosiPlannerDayMarkers(content: string): LosiPlannerDayMarkerMatch[] {
  return Array.from(content.matchAll(LOSI_PLANNER_DAY_MARKER_REGEX)).flatMap((match) => {
    const payload = parseLosiPlannerDayMarkerPayload(match[1] ?? '')
    if (!payload) return []

    return [{ payload, rawText: match[0] }]
  })
}

export function serializeLosiPlannerDayMarker(input: LosiPlannerDayMarkerPayload): string {
  const payload = parseLosiPlannerDayMarkerPayload(JSON.stringify(input))
  if (!payload) {
    throw new Error('A Losi planner day marker needs a date and at least one item')
  }

  return `[LOSI_PLANNER_DAY]${JSON.stringify(payload)}[/LOSI_PLANNER_DAY]`
}

// --------------

function readPlannerItems(
  value: unknown,
  dayKey: string,
  defaultSpaceId: string | null
): LosiPlannerDayMarkerItem[] {
  if (!Array.isArray(value)) return []

  return value
    .slice(0, MAX_ITEMS)
    .map((item, index) => readPlannerItem(item, dayKey, defaultSpaceId, index))
    .filter((item): item is LosiPlannerDayMarkerItem => item !== null)
}

function readPlannerItem(
  value: unknown,
  dayKey: string,
  defaultSpaceId: string | null,
  index: number
): LosiPlannerDayMarkerItem | null {
  const record = asRecord(value)
  if (!record) return null

  const title = readTextField(record, 'title', MAX_TEXT_LENGTH)
  const start = readDateTimeField(record, dayKey, ['start', 'startTime', 'start_time', 'time'])
  if (!title || !start) return null

  const rawEnd = readDateTimeField(record, dayKey, ['end', 'endTime', 'end_time'])
  const end = normalizeEndDate(start.iso, rawEnd)
  const eventId = readTextField(record, 'eventId', MAX_TEXT_LENGTH) ??
    readTextField(record, 'event_id', MAX_TEXT_LENGTH)
  const taskId = readTextField(record, 'taskId', MAX_TEXT_LENGTH) ??
    readTextField(record, 'task_id', MAX_TEXT_LENGTH)
  const explicitId = readTextField(record, 'id', MAX_TEXT_LENGTH)
  const id = explicitId || eventId || taskId || `planned-${index + 1}`
  const kind = readItemKind(record.kind)
  const source = readItemSource(record.source, eventId, kind)
  const status = readItemStatus(record.status, eventId)
  const description = readTextField(record, 'description', MAX_LONG_TEXT_LENGTH)
  const location = readTextField(record, 'location', MAX_TEXT_LENGTH)
  const spaceId = readTextField(record, 'spaceId', MAX_TEXT_LENGTH) ??
    readTextField(record, 'space_id', MAX_TEXT_LENGTH) ??
    defaultSpaceId

  return {
    ...(typeof record.allDay === 'boolean' ? { allDay: record.allDay } : {}),
    ...(description ? { description } : {}),
    end,
    ...(eventId ? { eventId } : {}),
    id,
    kind,
    ...(location ? { location } : {}),
    source,
    ...(spaceId ? { spaceId } : {}),
    start: start.iso,
    status,
    ...(taskId ? { taskId } : {}),
    title
  }
}

function readDateTimeField(
  record: Record<string, unknown>,
  dayKey: string,
  keys: string[]
): ParsedPlannerDateTime | null {
  for (const key of keys) {
    const value = record[key]
    if (typeof value !== 'string') continue

    const normalizedDate = normalizeDateTime(value, dayKey)
    if (normalizedDate) return normalizedDate
  }

  return null
}

function normalizeDateTime(value: string, dayKey: string) {
  const trimmed = value.trim()
  const fromTimeOnly = TIME_ONLY_PATTERN.test(trimmed)
  const valueToParse = fromTimeOnly ? `${dayKey}T${trimmed}:00` : trimmed
  const parsed = new Date(valueToParse)

  return Number.isFinite(parsed.getTime())
    ? {
        fromTimeOnly,
        iso: parsed.toISOString()
      }
    : null
}

function normalizeEndDate(startIso: string, endDateTime: ParsedPlannerDateTime | null) {
  const startDate = new Date(startIso)
  const endDate = endDateTime ? new Date(endDateTime.iso) : null

  if (endDate && endDate.getTime() > startDate.getTime()) {
    return endDate.toISOString()
  }

  if (endDate && endDateTime?.fromTimeOnly) {
    const nextDayEnd = new Date(endDate)
    nextDayEnd.setDate(nextDayEnd.getDate() + 1)

    if (nextDayEnd.getTime() > startDate.getTime()) {
      return nextDayEnd.toISOString()
    }
  }

  return new Date(startDate.getTime() + DEFAULT_DURATION_MINUTES * 60_000).toISOString()
}

function readDateKey(value: unknown) {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (DATE_ONLY_PATTERN.test(trimmed)) return trimmed

  const parsed = new Date(trimmed)
  if (!Number.isFinite(parsed.getTime())) return null

  return parsed.toISOString().slice(0, 10)
}

function readItemKind(value: unknown): LosiPlannerDayItemKind {
  if (typeof value === 'string' && ITEM_KIND_SET.has(value as LosiPlannerDayItemKind)) {
    return value as LosiPlannerDayItemKind
  }

  return 'event'
}

function readItemSource(
  value: unknown,
  eventId: string | null | undefined,
  kind: LosiPlannerDayItemKind
): LosiPlannerDayItemSource {
  if (typeof value === 'string' && ITEM_SOURCE_SET.has(value as LosiPlannerDayItemSource)) {
    return value as LosiPlannerDayItemSource
  }

  if (kind === 'task') return 'task'

  return eventId ? 'space' : 'losi'
}

function readItemStatus(
  value: unknown,
  eventId: string | null | undefined
): LosiPlannerDayItemStatus {
  if (typeof value === 'string' && ITEM_STATUS_SET.has(value as LosiPlannerDayItemStatus)) {
    return value as LosiPlannerDayItemStatus
  }

  return eventId ? 'accepted' : 'proposed'
}

function readTextField(record: Record<string, unknown>, key: string, maxLength: number) {
  const value = record[key]
  if (typeof value !== 'string') return null

  const cleaned = value
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!cleaned) return null

  return cleaned.slice(0, maxLength)
}

function normalizeEscapedJson(value: string) {
  return value
    .trim()
    .replace(/^\\\[/, '[')
    .replace(/\\\]$/, ']')
    .replace(/\\"/g, '"')
}

function stripJsonFence(value: string) {
  const trimmed = value.trim()
  const match = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)

  return (match?.[1] ?? trimmed).trim()
}

function parseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value) as unknown
    return asRecord(parsed)
  } catch {
    return null
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}
